import React, { useState, useEffect } from 'react';
import {
  ShieldCheckIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { api } from '../../services/authService';

const CreditScoreWidget = ({ clientId, clientDocument, onAnalysisComplete }) => {
  const [loading, setLoading] = useState(false);
  const [creditData, setCreditData] = useState(null);
  const [manualScore, setManualScore] = useState('');
  const [showManualInput, setShowManualInput] = useState(false);

  useEffect(() => {
    if (clientId) {
      loadExistingCreditData();
    }
  }, [clientId]);

  const loadExistingCreditData = async () => {
    try {
      const response = await api.get(`/api/clients/${clientId}`);
      const data = response.data;

      if (data.client && data.client.credit_score) {
        setCreditData({
          credit_score: data.client.credit_score,
          risk_level: data.client.risk_level,
          last_credit_check: data.client.last_credit_check,
          credit_status: data.client.credit_status
        });
      }
    } catch (error) {
      console.error('Erro ao carregar dados de crédito:', error);
      if (error.response?.status === 401) {
        toast.error('Sessão expirada. Faça login novamente.');
      }
    }
  };

  const handleAutomaticAnalysis = async () => {
    if (!clientDocument) {
      toast.error('Documento do cliente é necessário para análise');
      return;
    }

    setLoading(true);
    try {
      // Usar nova rota de análise via bureau
      const response = await api.post(`/api/credit-policy/bureau-analysis/${clientId}`);
      const data = response.data;

      setCreditData({
        credit_score: data.data.credit_score,
        risk_level: data.data.risk_level,
        last_credit_check: new Date().toISOString(),
        credit_status: 'approved'
      });

      toast.success('Análise de crédito concluída!');
      if (onAnalysisComplete) {
        onAnalysisComplete(data.data);
      }
    } catch (error) {
      console.error('Erro na análise:', error);
      const message = error.response?.data?.error || 'Erro ao realizar análise de crédito';
      toast.error(message);
      if (error.response?.status === 401) {
        toast.error('Sessão expirada. Faça login novamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleManualScore = async () => {
    if (!manualScore || manualScore < 0 || manualScore > 100) {
      toast.error('Score deve estar entre 0 e 100');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post(`/api/credit-policy/analyze-client/${clientId}`, {
        credit_score: parseInt(manualScore),
        credit_bureau_response: {
          source: 'manual',
          timestamp: new Date().toISOString(),
          entered_by: 'admin' // TODO: pegar usuário logado
        },
        credit_status: 'approved'
      });

      const data = response.data;
      setCreditData({
        credit_score: data.data.credit_score,
        risk_level: data.data.risk_level,
        last_credit_check: new Date().toISOString(),
        credit_status: 'approved'
      });

      toast.success('Score manual registrado!');
      setShowManualInput(false);
      setManualScore('');

      if (onAnalysisComplete) {
        onAnalysisComplete(data.data);
      }
    } catch (error) {
      console.error('Erro ao salvar score:', error);
      const message = error.response?.data?.error || 'Erro ao salvar score manual';
      toast.error(message);
      if (error.response?.status === 401) {
        toast.error('Sessão expirada. Faça login novamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Simulação de score baseado no documento (apenas para demo)
  const simulateCreditScore = (document) => {
    const numbers = document.replace(/\D/g, '');
    const sum = numbers.split('').reduce((acc, n) => acc + parseInt(n), 0);
    return Math.min(100, Math.max(0, (sum * 3) % 100));
  };

  const getRiskLevelLabel = (level) => {
    const labels = {
      'very_low': 'Muito Baixo',
      'low': 'Baixo',
      'medium': 'Médio',
      'high': 'Alto',
      'very_high': 'Muito Alto'
    };
    return labels[level] || level;
  };

  const getRiskLevelColor = (level) => {
    const colors = {
      'very_low': 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300 border-green-300',
      'low': 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300 border-blue-300',
      'medium': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300 border-yellow-300',
      'high': 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300 border-orange-300',
      'very_high': 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300 border-red-300'
    };
    return colors[level] || colors.medium;
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600 dark:text-green-400';
    if (score >= 60) return 'text-blue-600 dark:text-blue-400';
    if (score >= 40) return 'text-yellow-600 dark:text-yellow-400';
    if (score >= 20) return 'text-orange-600 dark:text-orange-400';
    return 'text-red-600 dark:text-red-400';
  };

  if (!clientId) {
    return (
      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center gap-3 mb-3">
          <ShieldCheckIcon className="w-6 h-6 text-gray-400" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Análise de Crédito
          </h3>
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Salve o cliente primeiro para realizar análise de crédito
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <ShieldCheckIcon className="w-6 h-6 text-blue-600 dark:text-blue-400" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Análise de Crédito
          </h3>
        </div>
        {creditData && (
          <button
            onClick={() => setShowManualInput(!showManualInput)}
            className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
          >
            {showManualInput ? 'Cancelar' : 'Inserir Manualmente'}
          </button>
        )}
      </div>

      {!creditData ? (
        <div className="space-y-4">
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <p className="text-sm text-blue-800 dark:text-blue-200 mb-3">
              Realize uma análise de crédito para determinar o nível de risco do cliente e as garantias necessárias.
            </p>
          </div>

          {showManualInput ? (
            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Score de Crédito (0-100)
              </label>
              <div className="flex gap-3">
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={manualScore}
                  onChange={(e) => setManualScore(e.target.value)}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Ex: 75"
                />
                <button
                  onClick={handleManualScore}
                  disabled={loading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Salvando...' : 'Salvar'}
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={handleAutomaticAnalysis}
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? (
                <>
                  <ArrowPathIcon className="w-5 h-5 animate-spin" />
                  Analisando...
                </>
              ) : (
                <>
                  <ShieldCheckIcon className="w-5 h-5" />
                  Realizar Análise Automática
                </>
              )}
            </button>
          )}

          <button
            onClick={() => setShowManualInput(!showManualInput)}
            className="w-full text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
          >
            {showManualInput ? 'Usar análise automática' : 'Ou insira o score manualmente'}
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Score e Risco */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
              <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Score de Crédito</p>
              <p className={`text-3xl font-bold ${getScoreColor(creditData.credit_score)}`}>
                {creditData.credit_score}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">de 100</p>
            </div>
            <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
              <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">Nível de Risco</p>
              <span className={`inline-block px-3 py-1 rounded-full text-sm font-bold border ${getRiskLevelColor(creditData.risk_level)}`}>
                {getRiskLevelLabel(creditData.risk_level)}
              </span>
            </div>
          </div>

          {/* Última Verificação */}
          {creditData.last_credit_check && (
            <div className="text-xs text-gray-600 dark:text-gray-400">
              Última verificação: {new Date(creditData.last_credit_check).toLocaleString('pt-BR')}
            </div>
          )}

          {/* Status */}
          <div className="flex items-center gap-2 text-sm">
            {creditData.credit_status === 'approved' && (
              <>
                <CheckCircleIcon className="w-5 h-5 text-green-500" />
                <span className="text-green-600 dark:text-green-400">Aprovado para contratos</span>
              </>
            )}
            {creditData.credit_status === 'under_review' && (
              <>
                <ExclamationTriangleIcon className="w-5 h-5 text-yellow-500" />
                <span className="text-yellow-600 dark:text-yellow-400">Em análise</span>
              </>
            )}
            {creditData.credit_status === 'rejected' && (
              <>
                <XCircleIcon className="w-5 h-5 text-red-500" />
                <span className="text-red-600 dark:text-red-400">Reprovado</span>
              </>
            )}
          </div>

          {/* Refazer Análise */}
          <button
            onClick={handleAutomaticAnalysis}
            disabled={loading}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <ArrowPathIcon className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refazer Análise
          </button>
        </div>
      )}
    </div>
  );
};

export default CreditScoreWidget;
